import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import classes from "./MapComponent.module.css";

import { 
  APIProvider, 
  Map, 
  AdvancedMarker,   
  InfoWindow,
  useMap
} from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import StudioProfile from "./StudioProfile/StudioProfile";
import GoogleMapsReadyGate from "./GoogleMapsReadyGate";
import { useFetchMapStudio } from '../hooks/useFetchMapStudio';
import { getStudioLowestPrice } from "../util/studioPricing";
import { getPublicStudioCoordinates } from "../util/locationPrivacy";
import { calculateMapBounds, calculateMapCenter } from "../util/searchGeo";

const DEFAULT_MAP_CENTER = { lat: 51.1657, lng: 10.4515 };
const DEFAULT_MAP_ZOOM = 12;
const MAP_BOUNDS_PADDING = 60;

const hasBoundsArea = (bounds) =>
  bounds && (bounds.north !== bounds.south || bounds.east !== bounds.west);

// Create a separate component for the map content
const MapContent = ({
  markers,
  selectedMarker,
  handleMarkerClick,
  selectedStudio,
  studioLoading,
  fromSearch,
  mapBounds,
  fallbackCenter,
}) => {
  const [markerRefsMap, setMarkerRefsMap] = useState({});
  const map = useMap();
  const clustererRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    if (hasBoundsArea(mapBounds) && typeof map.fitBounds === 'function') {
      map.fitBounds(mapBounds, MAP_BOUNDS_PADDING);
      return;
    }

    if (fallbackCenter && typeof map.setCenter === 'function') {
      map.setCenter(fallbackCenter);
      if (typeof map.setZoom === 'function') {
        map.setZoom(DEFAULT_MAP_ZOOM);
      }
    }
  }, [map, mapBounds, fallbackCenter]);

  // Create the markerClusterer when the map is available
  useEffect(() => {
    if (!map) return;

    // Create clusterer only once when map is available
    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({
        map,
        minimumClusterSize: 2
      });
    }

    // Cleanup function
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
    };
  }, [map]);

  // Clear markerRefsMap when markers prop changes (new studios loaded)
  useEffect(() => {
    setMarkerRefsMap({});
  }, [markers]);

  // Update markers in clusterer when markerRefsMap changes
  useEffect(() => {
    if (!clustererRef.current) return;

    // Clear all existing markers
    clustererRef.current.clearMarkers();
    
    // Add new markers from markerRefsMap
    const newMarkers = Object.values(markerRefsMap);
    if (newMarkers.length > 0) {
      clustererRef.current.addMarkers(newMarkers);
    }

  }, [markerRefsMap]); // Use markerRefsMap as dependency

  // Simplified marker ref callback
  const setMarkerRef = useCallback((marker, id) => {
    if (!marker) return;
    
    setMarkerRefsMap(prev => {
      // Only update if the marker is different
      if (prev[id] === marker) return prev;
      return { ...prev, [id]: marker };
    });
  }, []);

  // Cleanup markers when component unmounts
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
    };
  }, []);

  return (
    <>
      {markers.map((markerData) => {
        return (
          <AdvancedMarker
            key={markerData?.id}
            position={markerData?.position}
            clickable={true}
            ref={(marker) => setMarkerRef(marker, markerData?.id)}
            onClick={() => {
              handleMarkerClick(markerData.id);
            }}
          >
            {selectedMarker === markerData?.id ? (
              <div className={classes.markerContainerOpen}>
                <span className="semiboldSmall" style={{ color: "white" }}>{markerData?.price}€</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                  <circle cx="15" cy="15" r="15" fill="#1F1F1F"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M14.7892 19.085L9.0293 13.378L9.79874 12.6156L14.819 17.5898L20.2912 12.585L21.0293 13.3772L14.7979 19.0763L14.7892 19.085Z" fill="white"/>
                </svg>
              </div>
            ) : (
              <div 
                className={classes.markerContainer} 
                onClick={() => handleMarkerClick(markerData?.id)}
              >
                <span className="semiboldSmall" style={{ color: "white" }}>{markerData?.price}€</span>
              </div>
            )}
          </AdvancedMarker>
        );
      })}
      {selectedMarker && selectedStudio && !studioLoading && (
        <InfoWindow 
          position={markers.find(m => m.id === selectedMarker)?.position}
          onCloseClick={() => {
            handleMarkerClick(null);
          }}
        >
          <StudioProfile 
            studio={selectedStudio} 
            renderedBy="MapComponent"
            fromSearch={fromSearch}
          />
        </InfoWindow>
      )}
    </>
  );
};

// Main MapComponent
export const MapComponent = ({ studios, searchCenter = null, fromSearch = false }) => {
  const normalizedStudios = useMemo(() => (studios || []).map((studio) => {
    const coords = getPublicStudioCoordinates(studio);
    return coords ? { ...studio, ...coords } : { ...studio, latitude: null, longitude: null };
  }), [studios]);

  const center = useMemo(
    () => calculateMapCenter(normalizedStudios, searchCenter, {
      includeSearchCenterInBounds: Boolean(searchCenter),
    }) || DEFAULT_MAP_CENTER,
    [normalizedStudios, searchCenter]
  );

  const mapBounds = useMemo(
    () => calculateMapBounds(normalizedStudios, searchCenter, {
      includeSearchCenterInBounds: Boolean(searchCenter),
    }),
    [normalizedStudios, searchCenter]
  );

  const [selectedMarker, setSelectedMarker] = useState(null);

  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

  // Filter out studios without valid coordinates and create markers
  const markers = useMemo(() => normalizedStudios
    .filter(studio => studio.latitude != null && studio.longitude != null)
    .map(studio => ({
      id: studio.id,
      position: { lat: studio.latitude, lng: studio.longitude },
      price: getStudioLowestPrice(studio),
    })), [normalizedStudios]);

  // Use the hook to fetch detailed studio info when a marker is selected
  const { studio: selectedStudio, loading: studioLoading } = useFetchMapStudio(selectedMarker);

  const handleMarkerClick = (markerId) => {
    setSelectedMarker(markerId);
  };

  if (!GOOGLE_API_KEY) {
    return <div className={classes.mapContainer} />;
  }

  return (
    <div className={classes.mapContainer}>
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={['marker']}>
        <GoogleMapsReadyGate requireMarker>
          <Map
            mapId={'bf51a910020fa25a'}
            style={{ width: '100%', height: '100%' }}
            defaultCenter={center}
            defaultZoom={DEFAULT_MAP_ZOOM}
            gestureHandling={'greedy'}
            disableDefaultUI={true}
          >
            <MapContent 
              markers={markers}
              selectedMarker={selectedMarker}
              handleMarkerClick={handleMarkerClick}
              selectedStudio={selectedStudio}
              studioLoading={studioLoading}
              fromSearch={fromSearch}
              mapBounds={mapBounds}
              fallbackCenter={center}
            />
          </Map>
        </GoogleMapsReadyGate>
      </APIProvider>
    </div>
  );
};

export default MapComponent;
